import { useState } from 'react';
import { Plus, Pencil, XCircle } from 'lucide-react';
import { Button, Badge, Modal, Input, EmptyState } from '../../../ui';
import { toast } from '../../../ui/toast';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import { useCompanies, useBankAccounts } from '../../../hooks/useFinancialOps';
import {
  useCreditCards,
  useCreateCreditCard,
  useUpdateCreditCard,
  useRemoveCreditCard,
} from '../hooks/useCardInvoices';
import type { CreditCardDTO } from '../services/card-invoices.service';
import {
  useOpenFinanceAccounts,
  useOpenFinanceStatus,
  useOpenFinanceSync,
} from '../hooks/useOpenFinance';
import {
  ERROS_OPEN_FINANCE,
  MCPAI_CONSENTIMENTOS_URL,
  erroDaBusca,
  resumoDaBusca,
} from '../utils/openFinance';

/** Erro do salvar: 422 do vínculo (conta de outra empresa ou não CREDIT) e 502 do provedor. */
function erroAoSalvar(err: unknown): string {
  const resp = (err as { response?: { status?: number; data?: { message?: string } } } | null)
    ?.response;
  if (resp?.data?.message) {
    return resp.data.message;
  }
  if (resp?.status === 502) {
    return 'Open Finance indisponível: não deu para conferir a conta do cartão';
  }
  return 'Falha ao salvar o cartão';
}

interface CardFormData {
  companyId: string;
  name: string;
  issuer: string;
  brand: string;
  lastFour: string;
  closingDay: string;
  dueDay: string;
  defaultBankAccountId: string;
  notes: string;
  openFinanceAccountId: string;
}

const emptyForm = (companyId: string): CardFormData => ({
  companyId,
  name: '',
  issuer: '',
  brand: '',
  lastFour: '',
  closingDay: '',
  dueDay: '',
  defaultBankAccountId: '',
  notes: '',
  openFinanceAccountId: '',
});

export function CartoesPage() {
  const { data: companiesData } = useCompanies({ active: true });
  const companies = companiesData?.data ?? [];

  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const { data: cardsData, isLoading } = useCreditCards(selectedCompanyId);
  const { data: accountsData } = useBankAccounts(selectedCompanyId, { active: true });
  const accounts = accountsData?.data ?? [];
  const createCard = useCreateCreditCard();
  const updateCard = useUpdateCreditCard();
  const removeCard = useRemoveCreditCard();

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CardFormData>(emptyForm(''));

  const cards: CreditCardDTO[] = cardsData?.data ?? [];

  const ofAccounts = useOpenFinanceAccounts(showModal, 'CREDIT');
  const ofStatus = useOpenFinanceStatus();
  const ofSync = useOpenFinanceSync();
  const statusDe = (id: string) => ofStatus.data?.find((s) => s.cardId === id);

  function celulaOpenFinance(card: CreditCardDTO) {
    if (!card.openFinanceAccountId) {
      return '—';
    }
    const st = statusDe(card.id);
    return (
      <span>
        {st?.lastSyncAt ? `busca ${new Date(st.lastSyncAt).toLocaleString('pt-BR')}` : 'vinculado'}
        {st?.lastError && (
          <span className="block text-red-600 text-xs">
            {ERROS_OPEN_FINANCE[st.lastError] ?? st.lastError}
          </span>
        )}
      </span>
    );
  }

  function buscarAgora() {
    ofSync.mutate(undefined, {
      onSuccess: (r) => {
        if (!r || r.length === 0) {
          toast.error(
            'Nenhum cartão ou conta vinculado ao Open Finance. Edite um cartão e escolha a "Conta Open Finance".'
          );
          return;
        }
        const { texto, comErro } = resumoDaBusca(r);
        if (comErro) {
          toast.error(texto);
        } else {
          toast.success(texto);
        }
      },
      onError: (err) => toast.error(erroDaBusca(err)),
    });
  }

  function openCreate() {
    setEditingId(null);
    setForm(emptyForm(selectedCompanyId));
    setShowModal(true);
  }

  function openEdit(card: CreditCardDTO) {
    setEditingId(card.id);
    setForm({
      companyId: card.companyId,
      name: card.name ?? '',
      issuer: card.issuer ?? '',
      brand: card.brand ?? '',
      lastFour: card.lastFour ?? '',
      closingDay: card.closingDay ? String(card.closingDay) : '',
      dueDay: card.dueDay ? String(card.dueDay) : '',
      defaultBankAccountId: card.defaultBankAccountId ?? '',
      notes: card.notes ?? '',
      openFinanceAccountId: card.openFinanceAccountId ?? '',
    });
    setShowModal(true);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload: Record<string, any> = {
      companyId: form.companyId,
      name: form.name,
      issuer: form.issuer,
    };
    if (form.brand) {
      payload.brand = form.brand;
    }
    if (form.lastFour) {
      payload.lastFour = form.lastFour;
    }
    if (form.closingDay) {
      payload.closingDay = Number(form.closingDay);
    }
    if (form.dueDay) {
      payload.dueDay = Number(form.dueDay);
    }
    // R6: conta padrão é OPCIONAL — string vazia vira null no update (desvincula).
    payload.defaultBankAccountId = form.defaultBankAccountId || null;
    if (form.notes) {
      payload.notes = form.notes;
    }
    // Vazio desvincula (null), como na conta corrente.
    payload.openFinanceAccountId = form.openFinanceAccountId || null;
    try {
      if (editingId) {
        await updateCard.mutateAsync({ id: editingId, data: payload });
      } else {
        await createCard.mutateAsync(payload);
      }
    } catch (err) {
      toast.error(erroAoSalvar(err));
      return;
    }
    setShowModal(false);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div className="flex-1 max-w-xs">
          <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
            Empresa
          </label>
          <select
            className="h-9 w-full border border-border rounded-md px-3 text-sm bg-surface text-ink"
            value={selectedCompanyId}
            onChange={(e) => setSelectedCompanyId(e.target.value)}
          >
            <option value="">Selecione uma empresa...</option>
            {companies.map((c: any) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        {selectedCompanyId && (
          <div className="mt-5">
            <Button onClick={openCreate} size="sm">
              <Plus size={16} className="mr-1" /> Novo Cartão
            </Button>
          </div>
        )}
        <div className="mt-5 flex items-center gap-3">
          <Button size="sm" variant="ghost" onClick={buscarAgora} loading={ofSync.isPending}>
            Buscar agora
          </Button>
          <a
            href={MCPAI_CONSENTIMENTOS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-ink-secondary underline hover:text-ink"
          >
            Gerenciar consentimentos no mcp.ai
          </a>
        </div>
      </div>

      {!selectedCompanyId ? (
        <EmptyState
          title="Selecione uma empresa"
          body="Escolha uma empresa para ver os cartões de crédito."
        />
      ) : isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : cards.length === 0 ? (
        <EmptyState title="Nenhum cartão cadastrado" body="Clique em Novo Cartão para cadastrar." />
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-subtle text-ink-secondary">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Nome</th>
                <th className="px-4 py-2 text-left font-medium">Emissor</th>
                <th className="px-4 py-2 text-left font-medium">Bandeira / Final</th>
                <th className="px-4 py-2 text-left font-medium">Fechamento / Vencimento</th>
                <th className="px-4 py-2 text-left font-medium">Conta padrão</th>
                <th className="px-4 py-2 text-left font-medium">Open Finance</th>
                <th className="px-4 py-2 text-left font-medium">Status</th>
                <th className="px-4 py-2 text-left font-medium">Ações</th>
              </tr>
            </thead>
            <tbody>
              {cards.map((card) => (
                <tr key={card.id} className="border-t border-border hover:bg-subtle/50">
                  <td className="px-4 py-3 font-medium text-ink">{card.name}</td>
                  <td className="px-4 py-3 text-ink-secondary">{card.issuer}</td>
                  <td className="px-4 py-3 text-ink-secondary">
                    {card.brand ?? '—'} {card.lastFour ? `· ${card.lastFour}` : ''}
                  </td>
                  <td className="px-4 py-3 text-ink-secondary">
                    {card.closingDay ? `dia ${card.closingDay}` : '—'} /{' '}
                    {card.dueDay ? `dia ${card.dueDay}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-ink-secondary">
                    {card.defaultBankAccount
                      ? `${card.defaultBankAccount.bankName} — ${card.defaultBankAccount.name}`
                      : 'Todas as contas'}
                  </td>
                  <td className="px-4 py-3 text-ink-secondary">{celulaOpenFinance(card)}</td>
                  <td className="px-4 py-3">
                    <Badge tone={card.active ? 'accent' : 'neutral'}>
                      {card.active ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEdit(card)}
                        className="text-ink-secondary hover:text-ink-primary"
                        title="Editar"
                      >
                        <Pencil size={16} />
                      </button>
                      {card.active && (
                        <button
                          onClick={() => removeCard.mutate(card.id)}
                          className="text-ink-secondary hover:text-red-500"
                          title="Excluir/Desativar"
                        >
                          <XCircle size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editingId ? 'Editar Cartão' : 'Novo Cartão de Crédito'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Nome do Cartão *"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder='ex: "Nubank PJ — final 4589"'
            required
          />
          <Input
            label="Emissor *"
            value={form.issuer}
            onChange={(e) => setForm((f) => ({ ...f, issuer: e.target.value }))}
            placeholder="ex: Nubank, Itaú"
            required
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Bandeira"
              value={form.brand}
              onChange={(e) => setForm((f) => ({ ...f, brand: e.target.value }))}
              placeholder="Mastercard, Visa"
            />
            <Input
              label="Final (4 dígitos)"
              value={form.lastFour}
              onChange={(e) => setForm((f) => ({ ...f, lastFour: e.target.value }))}
              maxLength={4}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Dia do fechamento"
              type="number"
              min={1}
              max={31}
              value={form.closingDay}
              onChange={(e) => setForm((f) => ({ ...f, closingDay: e.target.value }))}
            />
            <Input
              label="Dia do vencimento"
              type="number"
              min={1}
              max={31}
              value={form.dueDay}
              onChange={(e) => setForm((f) => ({ ...f, dueDay: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
              Conta padrão de pagamento (opcional)
            </label>
            <select
              className="h-9 w-full border border-border rounded-md px-3 text-sm bg-surface text-ink"
              value={form.defaultBankAccountId}
              onChange={(e) => setForm((f) => ({ ...f, defaultBankAccountId: e.target.value }))}
            >
              <option value="">Todas as contas da empresa</option>
              {accounts.map((a: any) => (
                <option key={a.id} value={a.id}>
                  {a.bankName} — {a.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-ink-tertiary mt-1">
              A conciliação procura o débito da fatura só nesta conta quando definida.
            </p>
          </div>
          <div>
            <label
              htmlFor="of-card-account"
              className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5"
            >
              Conta Open Finance
            </label>
            <select
              id="of-card-account"
              className="h-9 w-full border border-border rounded-md px-3 text-sm bg-surface text-ink"
              value={form.openFinanceAccountId}
              onChange={(e) => setForm((f) => ({ ...f, openFinanceAccountId: e.target.value }))}
            >
              <option value="">Nenhuma</option>
              {(ofAccounts.data ?? [])
                .filter(
                  (a) =>
                    a.type === 'CREDIT' &&
                    a.companyId === form.companyId &&
                    (!a.linkedCardId || a.linkedCardId === editingId)
                )
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {`${a.companyName ?? 'empresa?'} · ${a.name ?? 'cartão'} · final ${a.last4 ?? '—'}`}
                  </option>
                ))}
            </select>
            <p className="text-xs text-ink-tertiary mt-1">
              A busca traz as faturas fechadas depois da última fatura deste cartão no CRM.
            </p>
          </div>
          <Input
            label="Observações"
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          />
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button type="submit" loading={createCard.isPending || updateCard.isPending}>
              {editingId ? 'Salvar' : 'Criar'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
